import type { Ref } from 'vue';
import type { Tone } from '#layers/ui/utils/emoji-glyph';
import { useLocalStorage } from '@vueuse/core';
import { isToneableShortcode, SKIN_TONE_VALUES } from '#layers/ui/utils/emoji-glyph';

interface UseEmojiSkinToneReturn {
	tone: Ref<Tone>;
	applyTone: (shortcode: string) => string;
}

const STORAGE_KEY = 'directus.emoji-skin-tone';

const VALID_TONES: ReadonlySet<Exclude<Tone, null>> = new Set(SKIN_TONE_VALUES);

function isValidTone(value: unknown): value is Exclude<Tone, null> {
	return typeof value === 'number' && VALID_TONES.has(value as Exclude<Tone, null>);
}

export function useEmojiSkinTone(): UseEmojiSkinToneReturn {
	const tone = useLocalStorage<Tone>(STORAGE_KEY, null, {
		serializer: {
			read: (raw) => {
				const parsed = Number(raw);
				if (!Number.isInteger(parsed)) return null;
				return isValidTone(parsed) ? parsed : null;
			},
			write: String,
		},
	});

	function applyTone(shortcode: string): string {
		if (!isToneableShortcode(shortcode)) return shortcode;
		const current = tone.value;
		if (current === null || current === 0) return shortcode;
		return `${shortcode}-tone${current}`;
	}

	return {
		tone,
		applyTone,
	};
}
